const onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
});

class VideoMedia extends HTMLElement {
    constructor() {
        super();
        this.player = null;
        this.isYouTube = false;
        this.isVimeo = false;
        this.posterOverlay = null;
        this.playPauseButton = null;
    }

    connectedCallback() {
        const videoId = this.dataset.videoId;
        const host = this.dataset.host;
        const autoplay = this.dataset.autoplay === 'true';
        const loop = this.dataset.loop === 'true';
        const muted = this.dataset.muted === 'true';
        const hasPoster = this.dataset.hasPoster === 'true';

        if (!videoId || !host) return;

        this.isYouTube = host === 'youtube';
        this.isVimeo = host === 'vimeo';
        const container = this.closest('.video-banner-media');
        if (container) {
            const wrapper = this.closest('.video-desktop-only, .video-mobile-only') || container;
            this.posterOverlay = wrapper.querySelector('.video-poster-overlay');

            this.playPauseButton = container.querySelector('.video-banner-play-pause-control-button');

            if (this.posterOverlay && !autoplay) {
                const playBtn = this.posterOverlay.querySelector('.video-poster-play-btn');

                if (this.playPauseButton && playBtn) {
                    playBtn.style.display = 'none';
                } else if (playBtn) {
                    playBtn.addEventListener('click', () => {
                        this.handlePosterPlayClick();
                    });
                }
            }

            if (this.playPauseButton) {
                this.playPauseButton.addEventListener('click', () => {
                    this.togglePlayPause();
                });
            }
        }

        if (this.isYouTube) {
            if (!hasPoster || autoplay) {
                this.initYouTubePlayer(videoId, autoplay, loop, muted, hasPoster);
            } else {
                this._pendingYTInit = { videoId, autoplay, loop, muted, hasPoster };
            }
        } else if (this.isVimeo) {
            if (!hasPoster || autoplay) {
                this.initVimeoPlayer(videoId, autoplay, loop, muted, hasPoster);
            } else {
                this._pendingVimeoInit = { videoId, autoplay, loop, muted, hasPoster };
            }
        }
    }

    handlePosterPlayClick() {
        if (this.posterOverlay) {
            this.posterOverlay.classList.add('hidden');

            setTimeout(() => {
                this.play();
            }, 300);
        } else {
            this.play();
        }

        this.updatePlayPauseButton(true);
    }

    updatePlayPauseButton(isPlaying) {
        if (this.playPauseButton) {
            const playIcon = this.playPauseButton.querySelector('.play-icon');
            const pauseIcon = this.playPauseButton.querySelector('.pause-icon');

            if (isPlaying) {
                playIcon?.classList.add('hidden');
                pauseIcon?.classList.remove('hidden');
            } else {
                playIcon?.classList.remove('hidden');
                pauseIcon?.classList.add('hidden');
            }
        }
    }

    togglePlayPause() {
        if (!this.player) {
            this.handlePosterPlayClick();
            return;
        }

        if (this.offsetParent === null) return;
        if (this.isYouTube) {
            const state = this.player.getPlayerState();
            if (state === YT.PlayerState.PLAYING) {
                this.pause();
            } else {
                if (this.posterOverlay && !this.posterOverlay.classList.contains('hidden')) {
                    this.posterOverlay.classList.add('hidden');
                    setTimeout(() => {
                        this.play();
                    }, 300);
                } else {
                    this.play();
                }
            }
        } else if (this.isVimeo) {
            this.player.getPaused().then(paused => {
                if (paused) {
                    if (this.posterOverlay && !this.posterOverlay.classList.contains('hidden')) {
                        this.posterOverlay.classList.add('hidden');
                        setTimeout(() => {
                            this.play();
                        }, 300);
                    } else {
                        this.play();
                    }
                } else {
                    this.pause();
                }
            });
        }
    }

    async initYouTubePlayer(videoId, autoplay, loop, muted, hasPoster) {
        try {
            if (!window.YT?.Player) {
                await this.loadScript('https://www.youtube.com/iframe_api');
                await onYouTubePromise;
            } else if (!window.YT.Player) {
                await onYouTubePromise;
            }

            const container = this.querySelector('div[id^="player-"]');
            if (!container) return;

            let loopInterval = null;

            const shouldAutoplay = autoplay && !hasPoster;

            this.player = new YT.Player(container, {
                videoId: videoId,
                width: '100%',
                height: '100%',
                playerVars: {
                    autoplay: shouldAutoplay ? 1 : 0,
                    mute: muted ? 1 : 0,
                    loop: 0,
                    controls: 0,
                    showinfo: 0,
                    rel: 0,
                    modestbranding: 1,
                    playsinline: 1,
                    iv_load_policy: 3,
                    disablekb: 1,
                    enablejsapi: 1,
                    origin: window.location.origin,
                    fs: 0,
                    cc_load_policy: 0
                },
                events: {
                    onReady: (event) => {
                        this.setAttribute('loaded', '');
                        if (shouldAutoplay) {
                            if (muted) {
                                event.target.mute();
                            }
                            event.target.playVideo();
                        }

                        if (loop) {
                            loopInterval = setInterval(() => {
                                if (this.player && typeof this.player.getCurrentTime === 'function' &&
                                    typeof this.player.getDuration === 'function') {
                                    const currentTime = this.player.getCurrentTime();
                                    const duration = this.player.getDuration();
                                    if (duration > 0 && currentTime > duration - 0.5) {
                                        this.player.seekTo(0);
                                    }
                                }
                            }, 100);
                        }
                    },
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.PLAYING) {
                            if (typeof window.pauseAllMedia === 'function') {
                                window.pauseAllMedia(this);
                            }
                            this.setAttribute('playing', '');
                            this.updatePlayPauseButton(true);
                        } else if (event.data === YT.PlayerState.PAUSED) {
                            this.removeAttribute('playing');
                            this.updatePlayPauseButton(false);
                        } else if (event.data === YT.PlayerState.ENDED) {
                            this.removeAttribute('playing');
                            this.updatePlayPauseButton(false);
                            if (loop) {
                                event.target.seekTo(0);
                                event.target.playVideo();
                            }
                        }
                    }
                }
            });

            this.loopInterval = loopInterval;
        } catch (error) {
            if (window.Shopify?.designMode) {
                console.warn('Error initializing YouTube player:', error);
            }
        }
    }

    async initVimeoPlayer(videoId, autoplay, loop, muted, hasPoster) {
        try {
            if (!window.Vimeo?.Player) {
                await this.loadScript('https://player.vimeo.com/api/player.js');
            }

            const container = this.querySelector('div[id^="player-"]');
            if (!container) return;

            const shouldAutoplay = autoplay && !hasPoster;

            this.player = new Vimeo.Player(container, {
                id: videoId,
                autoplay: shouldAutoplay,
                loop: loop,
                muted: muted,
                controls: false,
                title: false,
                byline: false,
                portrait: false,
                background: !hasPoster,
                responsive: true
            });

            this.player.ready().then(() => {
                this.setAttribute('loaded', '');
            }).catch(() => { });

            this.player.on('play', () => {
                if (typeof window.pauseAllMedia === 'function') {
                    window.pauseAllMedia(this);
                }
                this.setAttribute('playing', '');
                this.updatePlayPauseButton(true);
            });

            this.player.on('pause', () => {
                this.removeAttribute('playing');
                this.updatePlayPauseButton(false);
            });

            this.player.on('ended', () => {
                this.removeAttribute('playing');
                this.updatePlayPauseButton(false);
            });

            if (shouldAutoplay) {
                this.player.play().catch(() => { });
            }
        } catch (error) {
            if (window.Shopify?.designMode) {
                console.warn('Error initializing Vimeo player:', error);
            }
        }

    }

    loadScript(src) {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    play() {
        if (!this.player) {
            if (this.isYouTube && this._pendingYTInit) {
                this.initYouTubePlayer(
                    this._pendingYTInit.videoId,
                    this._pendingYTInit.autoplay,
                    this._pendingYTInit.loop,
                    this._pendingYTInit.muted,
                    this._pendingYTInit.hasPoster
                );
                this._pendingYTInit = null;
            } else if (this.isVimeo && this._pendingVimeoInit) {
                this.initVimeoPlayer(
                    this._pendingVimeoInit.videoId,
                    this._pendingVimeoInit.autoplay,
                    this._pendingVimeoInit.loop,
                    this._pendingVimeoInit.muted,
                    this._pendingVimeoInit.hasPoster
                );
                this._pendingVimeoInit = null;
            }
            return;
        }

        if (this.isYouTube && typeof this.player.playVideo === 'function') {
            this.player.playVideo();
        } else if (this.isVimeo && typeof this.player.play === 'function') {
            this.player.play();
        }
    }

    mute() {
        if (!this.player) return;
        if (this.isYouTube && typeof this.player.mute === 'function') {
            this.player.mute();
        } else if (this.isVimeo && typeof this.player.setMuted === 'function') {
            this.player.setMuted(true);
        }
    }

    unmute() {
        if (!this.player) return;
        if (this.isYouTube && typeof this.player.unMute === 'function') {
            this.player.unMute();
        } else if (this.isVimeo && typeof this.player.setMuted === 'function') {
            this.player.setMuted(false);
        }
    }

    isMuted() {
        if (!this.player) return Promise.resolve(true);
        if (this.isYouTube && typeof this.player.isMuted === 'function') {
            return Promise.resolve(this.player.isMuted());
        }
        if (this.isVimeo && typeof this.player.getMuted === 'function') {
            return this.player.getMuted();
        }
        return Promise.resolve(true);
    }

    pause() {
        if (!this.player) return;

        if (this.isYouTube && typeof this.player.pauseVideo === 'function') {
            this.player.pauseVideo();
        } else if (this.isVimeo && typeof this.player.pause === 'function') {
            this.player.pause();
        }
    }

    disconnectedCallback() {
        if (this.loopInterval) {
            clearInterval(this.loopInterval);
            this.loopInterval = null;
        }

        if (this.player) {
            if (this.isYouTube && typeof this.player.destroy === 'function') {
                this.player.destroy();
            } else if (this.isVimeo && typeof this.player.destroy === 'function') {
                this.player.destroy();
            }
        }
    }
}

customElements.define('video-media', VideoMedia);
document.addEventListener('DOMContentLoaded', function () {
    const videoBanners = document.querySelectorAll('.video-banner-media');

    videoBanners.forEach(banner => {
        const wrappers = banner.querySelectorAll('.video-desktop-only, .video-mobile-only');
        const contexts = wrappers.length > 0 ? wrappers : [banner];

        const playPauseButton = banner.querySelector('.video-banner-play-pause-control-button');
        const muteButton = banner.querySelector('.video-banner-mute-unmute-control-button');

        contexts.forEach(context => {
            const posterOverlay = context.querySelector('.video-poster-overlay');
            const video = context.querySelector('video');

            if (!video && !context.querySelector('video-media')) return;

            if (posterOverlay) {
                const playBtn = posterOverlay.querySelector('.video-poster-play-btn');

                if (playBtn) {
                    playBtn.addEventListener('click', function () {
                        posterOverlay.classList.add('hidden');

                        if (video) {
                            setTimeout(() => {
                                video.play().catch(() => { });
                            }, 300);
                        }

                        if (playPauseButton) {
                            const playIcon = playPauseButton.querySelector('.play-icon');
                            const pauseIcon = playPauseButton.querySelector('.pause-icon');
                            playIcon?.classList.add('hidden');
                            pauseIcon?.classList.remove('hidden');
                        }
                    });
                }
            }

            if (playPauseButton) {
                playPauseButton.addEventListener('click', function () {
                    if (!video || video.offsetParent === null) return;

                    if (video.paused) {
                        if (posterOverlay && !posterOverlay.classList.contains('hidden')) {
                            posterOverlay.classList.add('hidden');
                            setTimeout(() => {
                                video.play().catch(() => { });
                            }, 300);
                        } else {
                            video.play().catch(() => { });
                        }
                    } else {
                        video.pause();
                    }
                });

                if (video) {
                    video.addEventListener('play', function () {
                        if (video.offsetParent === null) return;
                        const playIcon = playPauseButton.querySelector('.play-icon');
                        const pauseIcon = playPauseButton.querySelector('.pause-icon');
                        playIcon?.classList.add('hidden');
                        pauseIcon?.classList.remove('hidden');
                    });

                    video.addEventListener('pause', function () {
                        if (video.offsetParent === null) return;
                        const playIcon = playPauseButton.querySelector('.play-icon');
                        const pauseIcon = playPauseButton.querySelector('.pause-icon');
                        playIcon?.classList.remove('hidden');
                        pauseIcon?.classList.add('hidden');
                    });
                }
            }

            if (muteButton) {
                muteButton.addEventListener('click', function () {
                    const muteIcon = muteButton.querySelector('.mute-icon');
                    const unmuteIcon = muteButton.querySelector('.unmute-icon');

                    if (video && video.offsetParent !== null) {
                        video.muted = !video.muted;
                        if (video.muted) {
                            muteIcon?.classList.add('hidden');
                            unmuteIcon?.classList.remove('hidden');
                        } else {
                            muteIcon?.classList.remove('hidden');
                            unmuteIcon?.classList.add('hidden');
                        }
                        return;
                    }

                    const videoMediaEl = context.querySelector('video-media');
                    if (!videoMediaEl) return;

                    const toggleVideoMediaMute = (isMutedNow) => {
                        if (isMutedNow) {
                            videoMediaEl.unmute();
                            muteIcon?.classList.remove('hidden');
                            unmuteIcon?.classList.add('hidden');
                        } else {
                            videoMediaEl.mute();
                            muteIcon?.classList.add('hidden');
                            unmuteIcon?.classList.remove('hidden');
                        }
                    };

                    if (typeof videoMediaEl.isMuted === 'function') {
                        const mutedState = videoMediaEl.isMuted();
                        if (mutedState && typeof mutedState.then === 'function') {
                            mutedState.then(isMutedNow => {
                                toggleVideoMediaMute(Boolean(isMutedNow));
                            }).catch(() => {
                                const isMutedNow = unmuteIcon && !unmuteIcon.classList.contains('hidden');
                                toggleVideoMediaMute(isMutedNow);
                            });
                        } else {
                            toggleVideoMediaMute(Boolean(mutedState));
                        }
                    } else {
                        const isMutedNow = unmuteIcon && !unmuteIcon.classList.contains('hidden');
                        toggleVideoMediaMute(isMutedNow);
                    }
                });
            }
        });
    });
});